import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { Member } from 'src/components/workspaces';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { useSidebarService } from '../api/useSidebarServices';

export type Workspace = {
  _id: string;
  workspaceId: string;
  name: string;
  open: boolean;
  isCustom: boolean;
  members: Member[];
  isPublic: boolean;
  owner: {
    memberId: string;
    firstname: string;
    lastname: string;
    email: string;
    userType: string;
    avatar: string;
  };
  icon: any;
  access: string;
  coverImage: string;
  description: string;
  collapseBoards: boolean;
  collapseDocuments: boolean;
  lms: boolean;
  files: {
    fileId: string;
    primaryFile: {
      id: string;
      type: string;
      view: string;
    };
    name: string;
    icon: any;
  }[];
};
export interface SidebarType {
  sharedPinnedFiles: {
    fileId: string;
    primaryFile: {
      id: string;
      type: string;
      view: string;
    };
    name: string;
    icon: any;
  }[];
  privatePinnedFiles: {
    fileId: string;
    primaryFile: {
      id: string;
      type: string;
      view: string;
    };
    name: string;
    icon: any;
  }[];
  lessonPinnedFiles: {
    slug: string;
    tag: any;
    name: string;
  }[];
  workspaces: Workspace[];
  isSharedOpen: boolean;
  isPrivateOpen: boolean;
  isLessonsOpen: boolean;
  isWorkspaceOpen: boolean;
  isSketchnoteLessonOpen: boolean;
  isCRMOpen: boolean;
}
export type SidebarState = {
  fetchSidebar: (mode?: string | string[]) => Promise<void>;
  pinFile: (file, pin) => void;
  selectedWorkspaceId: string;
  setSelectedWorkspaceId: (id: string) => Promise<void>;
  toggleShared: (toggle: boolean) => void;
  togglePrivate: (toggle: boolean) => void;
  toggleLesson: (toggle: boolean) => void;
  toggleWorkspace: (toggle: boolean) => void;
  toggleSketchnoteLesson: (toggle: boolean) => void;
  toggleCRM: (toggle: boolean) => void;
  toggleWorkspaceById: (toggle: boolean, id: string) => void;
} & SidebarType;

const { fetchSidebarDetails, pinFile } = useSidebarService();

const getInitialValues = {
  sharedPinnedFiles: [],
  privatePinnedFiles: [],
  workspaces: [],
  lessonPinnedFiles: [],
  isSharedOpen: false,
  isPrivateOpen: false,
  isLessonsOpen: false,
  isWorkspaceOpen: false,
  isSketchnoteLessonOpen: false,
  isCRMOpen: false,
};

export const useSidebarStore = create<
  SidebarState,
  [['zustand/devtools', SidebarState], ['zustand/immer', SidebarState]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      selectedWorkspaceId: '',
      setSelectedWorkspaceId: async (id) => {
        set((state) => {
          state.selectedWorkspaceId = id;
        });
        await useSidebarService().setSidebarWorkspace(id);
      },
      fetchSidebar: async (mode) => {
        const sidebarData = await fetchSidebarDetails(mode as string);
        if (sidebarData) {
          set((s) => {
            return { ...s, ...sidebarData };
          });
          set((state) => {
            state.isSharedOpen = sidebarData.isSharedOpen;
            state.isPrivateOpen = sidebarData.isPrivateOpen;
            state.isLessonsOpen = sidebarData.isLessonsOpen;
            state.isWorkspaceOpen = sidebarData.isWorkspaceOpen;
            state.selectedWorkspaceId = sidebarData.workspace;
          });
        }
      },
      pinFile: (file, pin) => {
        if (!file) return;
        const workspace = file.workspace?.id ?? file.workspace?._id;
        if (pin) {
          const pinnedFile = {
            fileId: file._id,
            primaryFile: {
              id: file._id,
              type: file.filetype,
              view: file.view,
            },
            name: file.title ?? file.name,
            icon: file.icon,
          };
          if (workspace) {
            // file is in a workspace
            set((state) => {
              const currentWorkspace = state.workspaces.find(
                (w) => w.workspaceId === workspace || w._id === workspace
              );
              if (currentWorkspace) {
                // check first if file is present in currentWorkspace
                const fileExists = currentWorkspace.files.find((f) => f.fileId === file._id);
                if (!fileExists) {
                  currentWorkspace.files.push(pinnedFile);
                }
              }
            });
          } else {
            // file is not in a workspace
            if (file.isShared) {
              // add file to shared pinned files
              set((state) => {
                // check first if file is present in sharedPinnedFiles
                const fileExists = state.sharedPinnedFiles.find((f) => f.fileId === file._id);
                if (!fileExists) {
                  state.sharedPinnedFiles.push(pinnedFile);
                }
              });
            } else {
              // add file to private pinned files
              set((state) => {
                // check first if file is present in privatePinnedFiles
                const fileExists = state.privatePinnedFiles.find((f) => f.fileId === file._id);
                if (!fileExists) {
                  state.privatePinnedFiles.push(pinnedFile);
                }
              });
            }
          }
        } else {
          if (workspace) {
            set((state) => {
              const currentWorkspace = state.workspaces.find(
                (w) => w.workspaceId === workspace || w._id === workspace
              );
              if (currentWorkspace) {
                // remove file from workspace
                currentWorkspace.files = currentWorkspace.files.filter(
                  (f) => f.fileId !== file._id
                );
              }
            });
          } else {
            if (file.isShared) {
              // remove file from shared pinned files
              set((state) => {
                state.sharedPinnedFiles = state.sharedPinnedFiles.filter(
                  (f) => f.fileId !== file._id
                );
              });
            } else {
              // remove file from private pinned files
              set((state) => {
                state.privatePinnedFiles = state.privatePinnedFiles.filter(
                  (f) => f.fileId !== file._id
                );
              });
            }
            // remove pin workspace when file is in workspace list
            set((state) => {
              state.workspaces.forEach((workspace) => {
                workspace.files = workspace.files.filter((f) => f.fileId !== file.fileId);
              });
            });
          }
        }
        pinFile({ fileId: file._id ?? file.fileId, pin });
      },
      toggleShared: (toggle) => {
        set((state) => {
          state.isSharedOpen = toggle;
        });
      },
      togglePrivate: (toggle) => {
        set((state) => {
          state.isPrivateOpen = toggle;
        });
      },
      toggleLesson: (toggle) => {
        set((state) => {
          state.isLessonsOpen = toggle;
        });
      },
      toggleWorkspace: (toggle) => {
        set((state) => {
          state.isWorkspaceOpen = toggle;
        });
      },

      toggleSketchnoteLesson: (toggle) => {
        set((state) => {
          state.isSketchnoteLessonOpen = toggle;
        });
      },

      toggleCRM: (toggle) => {
        set((state) => {
          state.isCRMOpen = toggle;
        });
      },
      toggleWorkspaceById: (toggle, id) => {
        set((state) => {
          const { workspaces } = state;

          const currentWorkspace = workspaces.find((workspace) => workspace.workspaceId === id);
          if (currentWorkspace) currentWorkspace.open = toggle;
        });
      },
      ...getInitialValues,
    }))
  )
);
